import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useIsMobileVariant } from '@dhl-ui/components'

import { useDebounce } from '@dhl/utils'

import { useSearchStore } from './useSearchStore.js'

type SearchContextType = {
  searchTerm: string | null
  setSearchTerm: (term: string) => void
  cancelSearch: () => void
  clearSearchInput: () => void
  isDesktop: boolean
}

const SearchContext = createContext<SearchContextType>({
  searchTerm: '',
  setSearchTerm: () => {},
  cancelSearch: () => {},
  clearSearchInput: () => {},
  isDesktop: false,
})

export const SearchContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { searchTerm, clearSearchState, clearSearchTerm, setSearchTerm: setStoreSearchTerm } = useSearchStore()

  const [searchTermInput, setSearchTerm] = useState(searchTerm)
  const debouncedSearchTerm = useDebounce(searchTermInput, 300)
  const isDesktop = !useIsMobileVariant()

  useEffect(() => {
    setStoreSearchTerm(debouncedSearchTerm.trim())
  }, [debouncedSearchTerm, setStoreSearchTerm])

  const cancelSearch = useCallback(() => {
    clearSearchState()
  }, [clearSearchState])

  const clearSearchInput = useCallback(() => {
    setSearchTerm('')
    clearSearchTerm()
  }, [clearSearchTerm])

  const context = useMemo(
    () => ({
      searchTerm: searchTermInput,
      setSearchTerm,
      cancelSearch,
      clearSearchInput,
      isDesktop,
    }),
    [cancelSearch, clearSearchInput, isDesktop, searchTermInput]
  )

  return <SearchContext.Provider value={context}>{children}</SearchContext.Provider>
}

export const useSearchContext = (): SearchContextType => useContext(SearchContext)
